import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DatePicker, DateValidationError, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { useUpdate } from '@refinedev/core';

import { useBoolean } from '@attackiq/hooks';

import useCurrentResource from '../hooks/useCurrentResource';

interface SetContentLastUpdatedDateProps<TResource> {
  resourceNameKey: { [TKey in keyof TResource]: TResource[TKey] extends string ? TKey : never }[keyof TResource];
}

interface BaseResource {
  last_updated: string;
}

export const SetContentLastUpdatedDate = <TResource extends BaseResource>({
  resourceNameKey
}: SetContentLastUpdatedDateProps<TResource>) => {
  const [isDialogOpen, { on: openDialog, off: closeDialog }] = useBoolean(false);
  const { id, resourceName, data } = useCurrentResource<TResource>();
  const [date, setDate] = useState<Date | null>(() => new Date());
  const [error, setError] = useState<DateValidationError | null>(null);
  const {
    mutateAsync,
    mutation: { isPending }
  } = useUpdate();

  const handleSet = async () => {
    if (id && date) {
      date.setHours(0, 0, 0, 0);

      await mutateAsync({
        id: id,
        resource: resourceName,
        values: {
          last_updated: date.toISOString()
        }
      });

      closeDialog();
    }
  };

  const [prevData, setPrevData] = useState(data);

  if (prevData !== data) {
    setPrevData(data);
    if (data) {
      setDate(new Date(data.last_updated));
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Button onClick={openDialog}>Set Last Updated Date</Button>
      <Dialog open={isDialogOpen} onClose={closeDialog}>
        <DialogTitle>{data ? String(data[resourceNameKey]) : ''}</DialogTitle>
        <DialogContent>
          <DatePicker
            label="Last Updated Date"
            value={date}
            onChange={setDate}
            onError={setError}
            slotProps={{
              textField: {
                sx: { mt: 0.5 }
              },
              actionBar: {
                actions: ['today']
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSet} disabled={isPending || !date || !!error}>
            Set
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
